import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  ArrowRight, Phone, EnvelopeSimple, Clock,
  Check, SpinnerGap, CaretDown, Sparkle,
  CheckCircle, PaperPlaneTilt
} from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    city_state: "",
    service_type: "",
    other_service: "",
    message: "",
    timeline: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const serviceTypes = [
    { value: "compliance", label: "Compliance & Accreditation Support" },
    { value: "clinical", label: "Clinical Operations & Interim Management" },
    { value: "education", label: "Staff Education & Preceptorship" },
    { value: "growth", label: "Growth & Census Development Strategies" },
    { value: "other", label: "Other" }
  ];

  const timelines = [
    { value: "immediately", label: "Immediately" },
    { value: "1-3-months", label: "1-3 Months" },
    { value: "3-6-months", label: "3-6 Months" },
    { value: "exploring", label: "Just Exploring" }
  ];

  const howItWorks = [
    { step: "01", title: "Register", description: "Fill out the form with your details" },
    { step: "02", title: "Book", description: "Schedule an appointment" },
    { step: "03", title: "Consult", description: "Meet with an expert" },
    { step: "04", title: "Free Initial Consult", description: "We determine your needs at no cost" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        organization: formData.city_state,
        phone: formData.phone,
        client_type: formData.service_type,
        message: `Service Interest: ${formData.service_type}${formData.service_type === 'other' ? ` - ${formData.other_service}` : ''}\nTimeline: ${formData.timeline}\n\nNeeds/Challenges: ${formData.message}`
      });
      setSuccess(true);
      setFormData({
        name: "",
        phone: "",
        email: "",
        city_state: "",
        service_type: "",
        other_service: "",
        message: "",
        timeline: ""
      });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="overflow-hidden">
      {/* Hero Section - Futuristic */}
      <section className="section-padding bg-slate-50 relative overflow-hidden" data-testid="contact-hero">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-mesh-gradient" />
        <div className="absolute inset-0 decorative-grid opacity-30" />
        
        <motion.div 
          className="absolute top-20 right-1/4 w-96 h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="max-w-7xl mx-auto container-padding relative">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="badge badge-accent mb-8">Get in Touch</span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-slate-900 mb-8 leading-[1.05] tracking-tight">
              Ready to Transform Your{" "}
              <span className="text-gradient">Operations?</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              We're here to help you enhance compliance, grow your census, 
              strengthen your clinical operations, and ensure your team delivers the highest quality care.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 -mt-8 relative z-10" data-testid="contact-info">
        <div className="max-w-5xl mx-auto container-padding">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.a
              href="tel:+17183046214"
              className="glass-card p-6 rounded-2xl flex items-center gap-4 hover:shadow-xl transition-all hover:-translate-y-1"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="contact-phone"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <Phone weight="fill" className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-display font-bold text-slate-900 mb-1">Call Us</h3>
                <p className="text-cyan-600 font-semibold">+1 (718) 304-6214</p>
              </div>
            </motion.a>

            <motion.a
              href="mailto:noblehospicesolutions@gmail.com"
              className="glass-card p-6 rounded-2xl flex items-center gap-4 hover:shadow-xl transition-all hover:-translate-y-1"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              data-testid="contact-email"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                <EnvelopeSimple weight="fill" className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-display font-bold text-slate-900 mb-1">Send Mail</h3>
                <p className="text-indigo-600 font-semibold text-sm">noblehospice<br/>solutions@gmail.com</p>
              </div>
            </motion.a>

            <motion.div
              className="glass-card p-6 rounded-2xl flex items-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              data-testid="contact-hours"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                <Clock weight="fill" className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-display font-bold text-slate-900 mb-1">Office Hours</h3>
                <p className="text-slate-600 text-sm">Mon - Fri: 8AM - 6PM PST</p>
                <p className="text-slate-600 text-sm">Sat - Sun: 8AM - 6PM EST</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Form Section */}
      <section className="section-padding bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Left Column - How It Works */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-3xl md:text-4xl text-slate-900 mb-10 tracking-tight">
                How This Works
              </h2>
              
              <div className="space-y-8 mb-12">
                {howItWorks.map((item, index) => (
                  <motion.div 
                    key={index} 
                    className="flex gap-5"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500/10 to-blue-500/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-cyan-600 font-bold">{item.step}</span>
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-slate-900">{item.title}</h3>
                      <p className="text-slate-600">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-2xl p-8 border border-cyan-500/20">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center mb-5">
                  <Sparkle weight="fill" className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-display font-bold text-slate-900 mb-3">Free Initial Consultation</h3>
                <p className="text-slate-600">
                  We will offer an initial consult for free to determine your needs and 
                  discuss how we can best support your organization.
                </p>
              </div>
            </motion.div>

            {/* Right Column - Form */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {/* Success Message */}
              {success && (
                <motion.div
                  className="mb-8 p-6 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-start gap-4"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="success-message"
                >
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle weight="fill" className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-emerald-800 mb-1">Thank You!</h3>
                    <p className="text-emerald-700">
                      Your enquiry has been received. A member of our team will reach out 
                      within 24 hours to schedule a complimentary discovery call.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Error Message */}
              {error && (
                <motion.div
                  className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="error-message"
                >
                  {error}
                </motion.div>
              )}

              <form
                onSubmit={handleSubmit}
                className="bg-white border border-slate-200 rounded-3xl p-8 md:p-10 shadow-xl"
                data-testid="contact-form"
              >
                <h2 className="font-display text-2xl md:text-3xl text-slate-900 mb-2 tracking-tight">
                  Consultation Enquiry Form
                </h2>
                <p className="text-slate-600 mb-8">
                  Please complete the form below and we'll get back to you within 24 hours.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Your Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="form-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Phone *</label>
                    <input
                      type="tel"
                      className="form-input"
                      placeholder="(555) 555-5555"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      data-testid="form-phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="form-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">City & State *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Austin, TX"
                      value={formData.city_state}
                      onChange={(e) => setFormData({ ...formData, city_state: e.target.value })}
                      required
                      data-testid="form-location"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Type of Services You're Interested In *</label>
                  <div className="relative">
                    <select
                      className="form-select pr-10"
                      value={formData.service_type}
                      onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                      required
                      data-testid="form-service"
                    >
                      <option value="">Select a service...</option>
                      {serviceTypes.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                    <CaretDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                {formData.service_type === 'other' && (
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-slate-900 mb-2">If 'Other', please specify</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Please describe..."
                      value={formData.other_service}
                      onChange={(e) => setFormData({ ...formData, other_service: e.target.value })}
                      data-testid="form-other"
                    />
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Briefly describe your current needs or challenges</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Tell us about your hospice's current situation and what you're hoping to achieve..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    data-testid="form-message"
                  />
                </div>

                <div className="mb-8">
                  <label className="block text-sm font-semibold text-slate-900 mb-2">How soon are you looking to start? *</label>
                  <div className="relative">
                    <select
                      className="form-select pr-10"
                      value={formData.timeline}
                      onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                      required
                      data-testid="form-timeline"
                    >
                      <option value="">Select timeline...</option>
                      {timelines.map((timeline) => (
                        <option key={timeline.value} value={timeline.value}>{timeline.label}</option>
                      ))}
                    </select>
                    <CaretDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold py-4 rounded-full hover:shadow-lg hover:shadow-cyan-500/30 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  disabled={loading}
                  data-testid="form-submit"
                >
                  {loading ? (
                    <>
                      <SpinnerGap className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <PaperPlaneTilt weight="fill" className="w-5 h-5" />
                      Submit Enquiry
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section - Dark Futuristic */}
      <section className="cta-section section-padding" data-testid="contact-cta">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-32 h-32 border border-cyan-500/30 rounded-full" />
          <div className="absolute bottom-10 right-10 w-48 h-48 border border-cyan-500/30 rounded-full" />
        </div>
        
        <motion.div 
          className="absolute top-0 right-1/4 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.4) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="relative max-w-4xl mx-auto container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl md:text-5xl text-white mb-8 tracking-tight">
              Your Trusted Healthcare Partner
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              A gateway for hospice owners, operators, and clinicians seeking to improve 
              patient care, grow census, optimize operations, and maintain regulatory excellence.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
